import { createAction, handleActions } from 'redux-actions'
import client from 'common/util/http'
import { loadStart, loadFinish, loadError } from 'common/redux/root'
import { loadUser } from 'common/redux/auth'
import { notify } from 'common/util/notify'
import toastr from 'toastr'

const STORE_NAME = 'account'

const RESET = `${STORE_NAME}/RESET`
const LOAD = `${STORE_NAME}/LOAD`
const SEARCH = `${STORE_NAME}/SEARCH`
const LOAD_PENDING_BROKER_USER_INVITE = `${STORE_NAME}/LOAD_PENDING_BROKER_USER_INVITE`
const FETCH_ACCOUNT_START = 'FETCH_ACCOUNT_START'
const FETCH_ACCOUNT_END = 'FETCH_ACCOUNT_END'

const load = createAction(LOAD)
export const search = createAction(SEARCH)
const loadPendingBrokerUserInvite = createAction(LOAD_PENDING_BROKER_USER_INVITE)
const fetchAccountStart = createAction(FETCH_ACCOUNT_START)
const fetchAccountEnd = createAction(FETCH_ACCOUNT_END)

const defaultErrorHandler = dispatch => error => {
  dispatch(loadError(error))
  return Promise.reject(error)
}

const defaultLoadHandler = dispatch => response => {
    dispatch(load(response))
    dispatch(loadFinish())
    return Promise.resolve(response)
}

const pendingBrokerUserInviteLoadHandler = dispatch => response => {
    dispatch(loadPendingBrokerUserInvite(response))
    dispatch(loadFinish())
    toastr.success('Success! Invites were resent.')
    return Promise.resolve(response)
}

export const accountLoadHandler = response => dispatch => {
  dispatch(load(response))
}

export function fetchAccount() {
  return dispatch => {
    dispatch(loadStart())
    dispatch(fetchAccountStart())
    return client.post('account')
    .then(response => {
      dispatch(load(response))
      dispatch(loadFinish())
      dispatch(fetchAccountEnd())
      return Promise.resolve(response)
    })
    .catch(err => {
      dispatch(loadError(err))
      dispatch(fetchAccountEnd())
      return Promise.reject(err)
    })
  }
}

export function fetchAccountAgentsPaged(pageNumber = 1, selectedFilter ="", searchTerm="", pageSize =50) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('account_agents_paged', {
      data: {
        page_number: pageNumber,
        page_size: pageSize,
        selected_filter: selectedFilter,
        search_term: searchTerm
      }})
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function fetchOverdueAgents() {
  return dispatch => {
    dispatch(loadStart())
    return client.post('account_overdue_agents')
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function fetchOverdueFeeFlags() {
  return dispatch => {
    dispatch(loadStart())
    return client.post('overdue_fee_flags')
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function hasUnpaidCloses() {
  return dispatch => {
    dispatch(loadStart())
    return client.post('overdue_unpaid_closes')
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function fetchSlimAccount() {
  return dispatch => {
    dispatch(loadStart())
    return client.post('slim_account')
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}


export function queueExportDownload(params) {
  return (dispatch) => {
    return client.post('create_lead_sources_report', {
      data: {...params}})
      .then(dispatch(defaultLoadHandler))
      .catch(dispatch(defaultErrorHandler))
  }
}

export function fetchLikelyCloses(timeframe, excludeOnHold = false, nav = {}) {
  return (dispatch, getState) => {
    let search_time = new Date().valueOf()
    dispatch(loadStart())
    dispatch(load({lastSearchTime: search_time}))
    return client.post('likely_closes_paged', {
      data: {
        timeframe,
        search_time,
        exclude_on_hold: excludeOnHold,
        nav: nav
      }})
      .then(response => {
        const { lastSearchTime } = getState().account
        if (response.search_time === lastSearchTime) {
          dispatch(load(response))
          dispatch(loadFinish())
        }
        return Promise.resolve(response)
      })
      .catch(dispatch(defaultErrorHandler))
  }
}

export function searchLikelyCloses(timeframe, query, filters, excludeOnHold, nav= {}) {
  return (dispatch, getState) => {
    let search_time = new Date().valueOf()
    dispatch(loadStart())
    dispatch(load({lastSearchTime: search_time}))
    return client.post('likely_closes_paged', {
      data: {
        timeframe,
        query,
        filters,
        search_time,
        exclude_on_hold: excludeOnHold,
        nav: nav
      }})
      .then(response => {
        const { lastSearchTime } = getState().account
        if (response.search_time === lastSearchTime) {
          dispatch(load(response))
          dispatch(loadFinish())
        }
        return Promise.resolve(response)
      })
      .catch(dispatch(defaultErrorHandler))
  }
}

export function fetchUnpaidCloses(timeframe, excludeOnHold = false, nav = {}) {
  return (dispatch, getState) => {
    let search_time = new Date().valueOf()
    dispatch(loadStart())
    dispatch(load({lastSearchTime: search_time}))
    return client.post('unpaid_closes_paged', {
      data: {
        timeframe,
        search_time,
        exclude_on_hold: excludeOnHold,
        nav: nav
      }})
      .then(response => {
        const { lastSearchTime } = getState().account
        if (response.search_time === lastSearchTime) {
          dispatch(load(response))
          dispatch(loadFinish())
        }
        return Promise.resolve(response)
      })
      .catch(dispatch(defaultErrorHandler))
  }
}

export function searchUnpaidCloses(timeframe, query, filters, excludeOnHold, nav = {}) {
  return (dispatch, getState) => {
    let search_time = new Date().valueOf()
    dispatch(loadStart())
    dispatch(load({lastSearchTime: search_time}))
    return client.post('unpaid_closes_paged', {
      data: {
        timeframe,
        query,
        filters,
        search_time,
        exclude_on_hold: excludeOnHold,
        nav: nav
      }})
      .then(response => {
        const { lastSearchTime } = getState().account
        if (response.search_time === lastSearchTime) {
          dispatch(load(response))
          dispatch(loadFinish())
        }
        return Promise.resolve(response)
      })
      .catch(dispatch(defaultErrorHandler))
  }
}

export function fetchLeadSources(timeframe, exclude_duplicates = true, nav ={}) {
  return (dispatch, getState) => {
    let search_time = new Date().valueOf()
    dispatch(loadStart())
    dispatch(load({lastSearchTime: search_time}))
    return client.post('lead_sources', {
      data: {
        timeframe,
        search_time,
        exclude_duplicates,
        nav: nav
      }})
    .then(response => {
      const { lastSearchTime } = getState().account
      if(response.search_time === lastSearchTime) {
        dispatch(load(response))
        dispatch(loadFinish())
      }
      return Promise.resolve(response)
    })
    .catch(dispatch(defaultErrorHandler))
  }
}

export function searchLeadSources(timeframe, query, filters, exclude_duplicates, nav={}) {
  return (dispatch, getState) => {
    let search_time = new Date().valueOf()
    dispatch(loadStart())
    dispatch(load({lastSearchTime: search_time}))
    return client.post('lead_sources', {
      data: {
        query,
        filters,
        timeframe,
        search_time,
        exclude_duplicates,
        nav: nav
      }
    })
      .then(response => {
        const { lastSearchTime } = getState().account
        if(response.search_time === lastSearchTime) {
          dispatch(load(response))
          dispatch(loadFinish())
        }
        return Promise.resolve(response)
      })
      .catch(dispatch(defaultErrorHandler))
  }
}

export function fetchSourceTypes() {
  return dispatch => {
    return client.post('source_types')
      .catch(dispatch(defaultErrorHandler))
  }
}

export function fetchStates() {
  return dispatch => {
    return client.post('states')
      .catch(dispatch(defaultErrorHandler))
  }
}

export function fetchMarkets() {
  return dispatch => {
    return client.post('markets')
      .catch(dispatch(defaultErrorHandler))
  }
}

export function fetchAgents() {
  return dispatch => {
    return client.post('agents')
      .catch(dispatch(defaultErrorHandler))
  }
}

export function fetchAddAgent(searchString) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('add_agent', {
      data: {
        search_string: searchString
      }
    })
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function fetchMarketGeo() {
  return dispatch => {
    client.post('market_geometry')
    .then(response => {
      dispatch(load(response))
    })
  }
}

export function removeAgent(agent_id, reason, comment) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('remove_agent', {
      data: {
        agent_id,
        reason,
        comment
      }
    })
      .then(dispatch(defaultLoadHandler))
      .catch(dispatch(defaultErrorHandler))
  }
}

export function updateTransaction(assignment_transaction_id, payload) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('update_transaction', {
      data: {
        assignment_transaction_id,
        ...payload
      }
    })
      .then(dispatch(defaultLoadHandler))
      .catch(dispatch(defaultErrorHandler))
  }
}

export function masqueradeAgent(agent_id) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('masquerade_agent', {
      data: {
        agent_id
      }
    })
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function resendBrokerInvite(broker_invite_hash, broker_inbound_hash) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('resend_broker_invite', {
      data: {
        broker_invite_hash,
        broker_inbound_hash,
      }
    })
    .then(response => {
      dispatch(load(response))
      dispatch(loadFinish())
      toastr.success('Success! Invites were resent.')
      return Promise.resolve(response)
    })
    .catch(dispatch(defaultErrorHandler))
  }
}

export function editBrokerInvite(broker_invite_hash, payload) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('edit_broker_invite', {
      data: {
        broker_invite_hash,
        email: payload.email,
        first_name: payload.first_name,
        last_name: payload.last_name,
      }
    })
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function cancelBrokerInvite(broker_invite_hash) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('cancel_broker_invite', {
      data: {
        broker_invite_hash,
      }
    })
    .then(response => {
      dispatch(load(response))
      dispatch(loadFinish())
      toastr.success('Invites were canceled.')
      return Promise.resolve(response)
    })
    .catch(dispatch(defaultErrorHandler))
  }
}

export function cancelBrokerInbound(broker_inbound_hash) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('cancel_broker_inbound', {
      data: {
        broker_inbound_hash,
      }
    })
    .then(response => {
      dispatch(load(response))
      dispatch(loadFinish())
      toastr.success('Invites were canceled.')
      return Promise.resolve(response)
    })
    .catch(dispatch(defaultErrorHandler))
  }
}

export function resendBrokerUserInvite(broker_invite_hash) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('resend_broker_user_invite', {
      data: {
        broker_invite_hash
      }
    })
    .then(dispatch(pendingBrokerUserInviteLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function cancelBrokerUserInvite(broker_user_invite_hash) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('cancel_broker_user_invite', {
      data: {
        broker_user_invite_hash,
      }
    })
    .then(response => {
      dispatch(load(response))
      dispatch(loadFinish())
      toastr.success('Invites were canceled.')
      return Promise.resolve(response)
    })
    .catch(dispatch(defaultErrorHandler))
  }
}

export function editBrokerUserInvite(broker_user_invite_hash, payload) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('edit_broker_user_invite', {
      data: {
        broker_user_invite_hash,
        email: payload.email,
        first_name: payload.first_name,
        last_name: payload.last_name,
      }
    })
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function removeBrokerUser(broker_user_id) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('remove_broker_user', {
      data: {
        broker_user_id: broker_user_id
      }
    })
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function fetchAgentDepartureAgreement(agent_id) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('agent_departure_agreement', {
      data: {
        agent_id,
      }
    })
    .then(dispatch(loadFinish()))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function checkJobStatus(queue, job_id) {
  return dispatch => {
    return client.post('check_job_status', {
        data: {
          queue,
          job_id
        }
    })
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function fetchLeadSourceStats(broker_hash) {
  return dispatch => {
    return client.post('lead_source_stats', {
      data: {
        broker_hash
      }
    })
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function fetchLeadSourceDetails(lead_source_type_id) {
  return dispatch => {
    return client.post('lead_source_details', {
      data: {
        lead_source_type_id
      }
    })
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function fetchQueuedReports() {
  return dispatch => {
    return client.post('queued_reports')
      .then(dispatch(defaultLoadHandler))
      .catch(dispatch(defaultErrorHandler))
  }
}

export function download_w9() {
  return dispatch => {
    dispatch(loadStart())
    return client.post('download_w9')
      .then(dispatch(defaultLoadHandler))
      .catch(dispatch(defaultErrorHandler))
  }
}

export function editPendingExperienceLevel(agent_experience_level, broker_invite_hash, broker_inbound_hash, email, should_resend_invite) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('edit_pending_experience_level', {
      data: {
        agent_experience_level,
        broker_invite_hash,
        broker_inbound_hash,
        should_resend_invite,
      }
    })
    .then(response => {
      if (should_resend_invite) {
        dispatch(load(response))
        notify.success(`New agent invite sent to ${email}`)
      }
      dispatch(loadFinish())
      return Promise.resolve(response)
    })
    .catch(dispatch(defaultErrorHandler))
  }
}

export function editAgentExperienceLevel(agent_experience_level, agent_id) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('edit_agent_experience_level', {
      data: {
        agent_experience_level,
        agent_id,
      }
    })
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function editAdminRole(admin_role, broker_user_id) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('edit_admin_role', {
      data: {
        admin_role,
        broker_user_id,
      }
    })
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function editAdminInformation(firstName, lastName, email, brokerUserId) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('edit_admin_information', {
      data: {
        firstName,
        lastName,
        email,
        brokerUserId
      }
    })
    .then(() => {
      toastr.success("Successfully updated admin")
      dispatch(defaultLoadHandler)
    })
    .catch((response) => {
      toastr.error(response.error.msg ? response.error.msg : "Email already in use")
      dispatch(defaultErrorHandler)
    })
  }
}

export function editAgentMVIPEnabled(transition_enabled, agent_id) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('edit_agent_mvip_enabled', {
      data: {
        transition_enabled: transition_enabled,
        agent_id: agent_id,
      }
    })
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function editAgentMVIPParticipant(mvip_participant, agent_id) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('edit_agent_mvip_participant', {
      data: {
        mvip_participant: mvip_participant,
        agent_id: agent_id,
      }
    })
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

export function updateBrokerContact(broker_hash, user_id) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('update_broker_contact', {
      data: {
        hash: broker_hash,
        user_id: user_id
      }
      }).then(response => {
        if (response.success) {
          toastr.success('Success! Account main contact updated')
        }
         dispatch(defaultLoadHandler)
        return Promise.resolve(response)
      })
      .catch(dispatch(defaultErrorHandler))
  }
}

export function acknowledgeTermsConditions() {
  return dispatch => {
    dispatch(loadStart())
    return client.post('acknowledge_terms_conditions', {
      data: {}
    })
    .then(response => {
      dispatch(loadUser(response))
    })
    .catch(dispatch(defaultErrorHandler))
  }
}

const initialState = {
  likelyClosesHidden: {},
  unpaidClosesHidden: {},
  loading: false
}

const handleLoadPendingBrokerContact = (state, { payload }) => {
  const { add_agent } = state
  const pendingBrokerContacts = _.get(add_agent, 'pending_broker_contacts', [])
  const { broker_user_invite } = payload
  if (!(broker_user_invite && broker_user_invite.hash)) {
    return state
  }

  if (!pendingBrokerContacts.length) {
    return {
      ...state,
      add_agent: {
        ...add_agent,
        pending_broker_contacts: [broker_user_invite]
      }
    }
  }

  const idx = _.findIndex(pendingBrokerContacts, pbc => pbc.hash === broker_user_invite.hash)

  if (idx === -1) {
    return {
      ...state,
      add_agent: {
        ...add_agent,
        pending_broker_contacts: [...pendingBrokerContacts, broker_user_invite]
      }
    }
  }

  return {
    ...state,
    add_agent: {
      ...add_agent,
      pending_broker_contacts: [
        ...pendingBrokerContacts.slice(0, idx),
        {...broker_user_invite},
        ...pendingBrokerContacts.slice(idx+1)
      ]
    }
  }
}

export default handleActions({
    [LOAD]: (state, { payload }) => ({ ...state, ...payload }),
    [RESET]: () => ({ ...initialState }),
    [SEARCH]: (state, { payload }) => ({ ...state, ...payload }),
    [LOAD_PENDING_BROKER_USER_INVITE]: handleLoadPendingBrokerContact,
    [FETCH_ACCOUNT_START]: state => ({
      ...state,
      loading: true
    }),
    [FETCH_ACCOUNT_END]: state => ({
      ...state,
      loading: false
    })
}, initialState)
